import { Injectable, inject } from '@angular/core'
import { SelectOption } from '@models/response/select-object-options'
import { WorkspaceTemplateConfigurationService } from '@app/feature/header/workspace-controls/workspace-services/workspace-template-configuration.service'
import { Observable } from 'rxjs'
import { switchMap, take, tap, map } from 'rxjs/operators'
import {
    CreateWorkspaceData,
    Field,
    Workspace,
    getSotUpdateConfiguration,
    NewWorkspaceTemplateData,
    Schema,
    UpdateWorkspaceChanges,
    WorkspaceViewRecord,
    CreateFieldModel,
    FieldEntities,
} from '@core/models'
import {
    AccessModel,
    WorkspaceCreateModel,
    WorkspaceCreateSotType,
    WorkspaceDeleteModel,
    WorkspaceUpdateModel,
} from '@models/request'
import { WorkspaceFacadeService } from '@services/store-facade'
import { NavigatorService } from '@services/navigator.service'
import { keys, map as lodashMap } from 'lodash-es'

@Injectable({ providedIn: 'root' })
export class WorkspaceService {
    private readonly workspaceFacadeService = inject(WorkspaceFacadeService)
    private readonly templateConfigurationService = inject(WorkspaceTemplateConfigurationService)
    private readonly navigatorService = inject(NavigatorService)

    createWorkspace(workspaceData: Observable<CreateWorkspaceData>) {
        return workspaceData.pipe(
            switchMap((data) => {
                const model: WorkspaceCreateModel = {
                    access: data.access,
                    configuration: getSotUpdateConfiguration(data.configuration),
                    fields: data.fields,
                    views: data.views,
                }

                return this.workspaceFacadeService.createWorkspaceRequest(model)
            }),
        )
    }

    updateWorkspace(workspace: Workspace, changes: UpdateWorkspaceChanges, access?: AccessModel) {
        const model: WorkspaceUpdateModel = {
            record_guid: workspace.guid,
            record_revision: workspace.revision,
            configuration: getSotUpdateConfiguration(changes.configuration),
            access,
        }

        return this.workspaceFacadeService.updateWorkspaceRequest(model)
    }

    deleteWorkspace(workspace: Workspace) {
        return this.workspaceFacadeService.selectSelectedWorkspaceGuid$.pipe(
            take(1),
            switchMap((selectedWorkspaceGuid) => {
                const model: WorkspaceDeleteModel = {
                    record_guid: workspace.guid,
                    record_revision: workspace.revision,
                }

                return this.workspaceFacadeService.deleteWorkspaceRequest(model).pipe(
                    tap(() => {
                        if (selectedWorkspaceGuid === workspace.guid) {
                            this.navigatorService.goToGlobalWorkspace()
                        }
                    }),
                )
            }),
        )
    }

    getNewWorkspaceTemplate(
        views?: WorkspaceViewRecord,
        field_schemas?: Schema[],
        fields?: Record<string, FieldEntities>,
        inactiveFields?: string[],
    ): Observable<NewWorkspaceTemplateData> {
        return this.templateConfigurationService.getTemplate$().pipe(
            map((workspaceTemplate) => {
                return {
                    views: views || workspaceTemplate.views,
                    fields: this.combineWorkspaceFields(
                        field_schemas || workspaceTemplate.field_related_schemas,
                        fields || workspaceTemplate.fields_by_schemas,
                        inactiveFields,
                    ),
                }
            }),
        )
    }

    private combineWorkspaceFields(
        fieldSchemas: Schema[],
        fields: Record<string, FieldEntities>,
        inactiveFields?: string[],
    ) {
        return fieldSchemas.reduce(
            (res, schema) => {
                const object_type_code = schema.object_type_code as WorkspaceCreateSotType
                const schemaFields = fields[schema.guid]

                res[object_type_code] = keys(schemaFields)
                    .filter((key) => {
                        if (!inactiveFields) return true

                        return !inactiveFields.includes(key)
                    })
                    .map((next) => {
                        const field = schemaFields[next]

                        return this.combineWorkspaceCreateField(field)
                    })

                return res
            },
            {} as Record<WorkspaceCreateSotType, Partial<CreateFieldModel>[]>,
        )
    }

    private combineWorkspaceCreateField(field: Field) {
        return {
            name: field.name ?? field.field_type_code,
            field_type_code: field.field_type_code,
            shared_with_workspaces: field.shared_with_workspaces,
            is_on_top: field.is_on_top,
            acl: null,
            is_required: field.is_required,
            configure_json: field.configure_json,
            select_object_field: this.parseSelectOptionsToArray(field),
            default_value: field.default_value,
            system_configuration: field.system_configuration,
        }
    }

    private parseSelectOptionsToArray(field: Field): SelectOption[] | undefined {
        if (field.select_object_field_sorted) return field.select_object_field_sorted

        return lodashMap(field.select_object_field, (value, guid) => ({ guid, ...value }))
    }
}
